import { useEffect, useRef } from "react"
import { useSearchParams } from "react-router-dom"
import {
  captureAffiliate,
  claimAffiliate,
  type TrackingData,
} from "@/shared/utils/affiliate/fingerprint"

/**
 * useAffiliateTracking — browser fingerprint-based affiliate attribution.
 *
 * Ported from v1's `src/hooks/useAffiliateTracking.ts`.
 *
 * Two-phase system:
 *
 * Phase 1 — Capture (web landing page):
 *   User lands with ?affiliate=xxx or ?utm_source=xxx etc.
 *   → saves params to localStorage
 *   → POSTs fingerprint + params to /affiliate/capture
 *
 * Phase 2 — Claim (app open, including Capacitor WebView):
 *   If no URL params present, attempts to match the device fingerprint
 *   against previously captured records.
 *   → POSTs fingerprint to /affiliate/claim
 *   → saves matched tracking data to localStorage
 *
 * Both phases run only once per session (refs prevent re-execution).
 */

const TRACKING_KEYS = [
  "affiliate",
  "utm_source",
  "utm_content",
  "utm_medium",
  "utm_term",
  "utm_campaign",
  "fbclid",
  "gclid",
] as const

export function useAffiliateTracking(): void {
  const [searchParams] = useSearchParams()
  const hasAttemptedCapture = useRef(false)
  const hasAttemptedClaim = useRef(false)

  // Phase 1 — Capture from URL params
  useEffect(() => {
    if (hasAttemptedCapture.current) return

    const trackingData: TrackingData = {}
    let hasAnyParam = false

    // "ref" is a marketing-friendly alias for "affiliate"
    const ref = searchParams.get("ref")
    if (ref) {
      trackingData.affiliate = ref
      hasAnyParam = true
    }

    for (const key of TRACKING_KEYS) {
      const value = searchParams.get(key)
      if (value) {
        trackingData[key] = value
        hasAnyParam = true
      }
    }

    if (!hasAnyParam) return

    hasAttemptedCapture.current = true

    // Persist to localStorage immediately
    for (const [key, value] of Object.entries(trackingData)) {
      if (value) {
        localStorage.setItem(`totodo_analytics_${key}`, value)
      }
    }

    captureAffiliate(trackingData)
      .then((id) => {
        if (id) {
          console.log("[Affiliate] Tracking data captured:", trackingData)
        }
      })
      .catch((err) => {
        console.warn("[Affiliate] Capture error:", err)
      })
  }, [searchParams])

  // Phase 2 — Claim (no URL params → try fingerprint match)
  useEffect(() => {
    if (hasAttemptedClaim.current) return
    if (hasAttemptedCapture.current) return // If we captured, don't also claim

    // Check if URL has any tracking params — if so, skip claim
    const hasUrlParams =
      searchParams.get("ref") ||
      TRACKING_KEYS.some((k) => searchParams.get(k))
    if (hasUrlParams) return

    hasAttemptedClaim.current = true

    claimAffiliate()
      .then((result) => {
        if (result?.trackingData) {
          console.log("[Affiliate] Tracking data claimed:", result.trackingData)
          for (const [key, value] of Object.entries(result.trackingData)) {
            if (value) {
              localStorage.setItem(`totodo_analytics_${key}`, value)
            }
          }
        }
      })
      .catch((err) => {
        // 404 (no match) is silently expected
        if (!String(err).includes("404")) {
          console.warn("[Affiliate] Claim error:", err)
        }
      })
  }, [searchParams])
}
